<script setup>
import { getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import Card from 'primevue/card';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const props = defineProps({
    orderNumber: { type: String, required: true },
    bankDetails: { type: Object, required: true },
    total: { type: Number, required: true },
    currency: { type: String, required: true },
    orderId: { type: Number, required: true },
});

const formatTotal = () => {
    // Round to nearest 5 cents in integer space to avoid floating point errors
    const roundedCents = Math.ceil(props.total / 5) * 5;
    const value = roundedCents / 100;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: props.currency,
        minimumFractionDigits: 2,
    }).format(value);
};

const copy = async (text, label) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.add({ severity: 'success', summary: $t('Copied'), detail: `${label} ${$t('copied to clipboard')}`, life: 2000 });
    } catch {
        toast.add({ severity: 'warn', summary: $t('Error'), detail: $t('Could not copy'), life: 2000 });
    }
};

const goToOrder = () => {
    router.visit(proxy.route('client.orders.show', { order: props.orderId }));
};
</script>

<template>
    <Card class="wise-instructions">
        <template #title>
            <div class="flex items-center gap-2">
                <i class="pi pi-send text-blue-600 text-xl"></i>
                <span>{{ $t('Complete your Wise Transfer') }}</span>
            </div>
        </template>

        <template #content>
            <p class="text-surface-600 mb-5">
                {{ $t('Your order has been created. Transfer the amount below using the bank details provided and include the reference in the description.') }}
            </p>

            <!-- Bank details -->
            <div class="border border-blue-200 rounded-xl p-5 bg-blue-50 mb-5 space-y-3">
                <h3 class="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <i class="pi pi-building-columns"></i>
                    {{ $t('Bank Details') }}
                </h3>

                <div v-if="bankDetails.bank_name" class="flex items-center justify-between">
                    <span class="text-sm text-surface-600">{{ $t('Bank') }}</span>
                    <div class="flex items-center gap-2">
                        <span class="font-medium">{{ bankDetails.bank_name }}</span>
                        <Button icon="pi pi-copy" size="small" text @click="copy(bankDetails.bank_name, $t('Bank name'))" />
                    </div>
                </div>

                <div v-if="bankDetails.account_holder" class="flex items-center justify-between">
                    <span class="text-sm text-surface-600">{{ $t('Account Holder') }}</span>
                    <div class="flex items-center gap-2">
                        <span class="font-medium">{{ bankDetails.account_holder }}</span>
                        <Button icon="pi pi-copy" size="small" text @click="copy(bankDetails.account_holder, $t('Account holder'))" />
                    </div>
                </div>

                <div v-if="bankDetails.iban" class="flex items-center justify-between">
                    <span class="text-sm text-surface-600">IBAN</span>
                    <div class="flex items-center gap-2">
                        <span class="font-medium font-mono">{{ bankDetails.iban }}</span>
                        <Button icon="pi pi-copy" size="small" text @click="copy(bankDetails.iban, 'IBAN')" />
                    </div>
                </div>

                <div v-if="bankDetails.bic" class="flex items-center justify-between">
                    <span class="text-sm text-surface-600">BIC / SWIFT</span>
                    <div class="flex items-center gap-2">
                        <span class="font-medium font-mono">{{ bankDetails.bic }}</span>
                        <Button icon="pi pi-copy" size="small" text @click="copy(bankDetails.bic, 'BIC')" />
                    </div>
                </div>

                <div v-if="bankDetails.bank_code" class="flex items-center justify-between">
                    <span class="text-sm text-surface-600">{{ $t('Bank Code') }}</span>
                    <div class="flex items-center gap-2">
                        <span class="font-medium font-mono">{{ bankDetails.bank_code }}</span>
                        <Button icon="pi pi-copy" size="small" text @click="copy(bankDetails.bank_code, $t('Bank Code'))" />
                    </div>
                </div>

                <div v-if="bankDetails.agency_number" class="flex items-center justify-between">
                    <span class="text-sm text-surface-600">{{ $t('Branch / Routing') }}</span>
                    <div class="flex items-center gap-2">
                        <span class="font-medium font-mono">{{ bankDetails.agency_number }}</span>
                        <Button icon="pi pi-copy" size="small" text @click="copy(bankDetails.agency_number, $t('Branch / Routing'))" />
                    </div>
                </div>

                <div v-if="bankDetails.account_number" class="flex items-center justify-between">
                    <span class="text-sm text-surface-600">{{ $t('Account Number') }}</span>
                    <div class="flex items-center gap-2">
                        <span class="font-medium font-mono">{{ bankDetails.account_number }}</span>
                        <Button icon="pi pi-copy" size="small" text @click="copy(bankDetails.account_number, $t('Account Number'))" />
                    </div>
                </div>

                <div v-if="bankDetails.company_legal_name" class="flex items-center justify-between">
                    <span class="text-sm text-surface-600">{{ $t('Legal Name') }}</span>
                    <div class="flex items-center gap-2">
                        <span class="font-medium">{{ bankDetails.company_legal_name }}</span>
                        <Button icon="pi pi-copy" size="small" text @click="copy(bankDetails.company_legal_name, $t('Legal Name'))" />
                    </div>
                </div>

                <div v-if="bankDetails.company_document_number" class="flex items-center justify-between">
                    <span class="text-sm text-surface-600">{{ bankDetails.company_document_type || $t('Tax ID') }}</span>
                    <div class="flex items-center gap-2">
                        <span class="font-medium font-mono">{{ bankDetails.company_document_number }}</span>
                        <Button icon="pi pi-copy" size="small" text @click="copy(bankDetails.company_document_number, bankDetails.company_document_type || $t('Tax ID'))" />
                    </div>
                </div>

                <div class="flex items-center justify-between">
                    <span class="text-sm text-surface-600">{{ $t('Amount') }}</span>
                    <span class="font-semibold text-primary">{{ formatTotal() }}</span>
                </div>

                <div v-if="bankDetails.payment_link" class="pt-3 mt-2 border-t border-blue-200">
                    <a
                        :href="bankDetails.payment_link"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="block text-center font-medium text-blue-700 hover:text-blue-900 text-sm"
                    >
                        <i class="pi pi-external-link mr-2"></i>
                        {{ $t('Pay quickly with Wise') }}
                    </a>
                </div>
            </div>

            <!-- Reference — highlighted -->
            <div class="bg-blue-600 rounded-xl p-5 mb-5 text-center text-white">
                <div class="text-sm uppercase tracking-widest mb-1 opacity-80">
                    ⚠️ {{ $t('Required in transfer description') }}
                </div>
                <div class="text-4xl font-bold font-mono tracking-[0.3em] my-2">{{ orderNumber }}</div>
                <div class="text-xs opacity-70 mb-3">{{ $t('Copy this reference exactly') }}</div>
                <Button
                    :label="$t('Copy Reference')"
                    icon="pi pi-copy"
                    severity="contrast"
                    size="small"
                    @click="copy(orderNumber, $t('Reference'))"
                />
            </div>

            <!-- Info -->
            <div class="bg-surface-50 rounded-lg p-4 text-sm text-surface-600 space-y-2 mb-5">
                <p><i class="pi pi-check-circle text-green-500 mr-2"></i>{{ $t('Your order will be activated automatically once we receive the transfer.') }}</p>
                <p><i class="pi pi-envelope text-blue-500 mr-2"></i>{{ $t('You will receive a confirmation email when the payment is processed.') }}</p>
            </div>

            <Button
                :label="$t('View Order Status')"
                icon="pi pi-arrow-right"
                class="w-full"
                @click="goToOrder"
            />
        </template>
    </Card>
</template>
